<template>
  <div class="pricing-enhancements">
    <div class="price-per-night">
      <h4>Price per Night</h4>
      <div class="price-display">
        <span class="price-value">${{ formatPriceAmount(pricePerNight) }}</span>
        <span class="price-label">per night</span>
      </div>
    </div>
    
    <div v-if="savings > 0" class="savings-calculator">
      <h4>Potential Savings</h4>
      <div class="savings-display">
        <span class="savings-amount">Save up to ${{ formatPriceAmount(savings) }}</span>
        <span class="savings-detail">Compared to 30-day peak for this cabin</span>
        <span class="savings-explainer">Based on Cruise Signals 30-day price tracking for this specific cabin category and sail date, not cruise line list prices.</span>
      </div>
    </div>
    
    <div v-if="bestBookingTime" class="booking-insight">
      <h4>Best Time to Book</h4>
      <div class="insight-content">
        <i class="fas fa-lightbulb"></i>
        <p>{{ bestBookingTime }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { peakIsCoherent } from '@/utils/cabinPeak';
import { bookingTimeCopy } from '@/utils/bookingTrend';

import { formatPriceAmount } from '@/utils/formatMoney';
export default {
  name: 'PricingEnhancements',
  props: {
    currentPrice: {
      type: Number,
      required: true
    },
    lowestPrice: {
      type: Number,
      required: false
    },
    highestPrice: {
      type: Number,
      required: false
    },
    duration: {
      type: Number,
      required: true
    },
    priceHistory: {
      type: Array,
      default: () => []
    },
    dealStatus: {
      type: Object,
      default: null
    },
    sailDate: {
      type: String,
      default: null
    },
    // Detail "Save up to" from the prices API. null means omit. Undefined
    // means this response predates the field.
    potentialSavings: {
      default: undefined
    },
    // Points behind the single cabin card (Current / Lowest / Highest).
    // Empty when more than one cabin is on screen, so the sentence does
    // not pick a direction those tiles do not share.
    observedSeries: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const pricePerNight = computed(() => {
      if (!props.currentPrice || !props.duration) return 0;
      return Math.round(props.currentPrice / props.duration);
    });
    
    const toDateOnly = (value) => {
      if (!value) return null;
      return String(value).slice(0, 10);
    };

    const savings = computed(() => {
      // The prices API already applied the cabin-order guard. null is an
      // explicit omit — do not rebuild the dollar from the chart max.
      if (props.potentialSavings === null) return 0;
      if (typeof props.potentialSavings === 'number' && Number.isFinite(props.potentialSavings)) {
        return props.potentialSavings > 0 ? Math.round(props.potentialSavings) : 0;
      }

      // Preferred source: server-provided price_drop_amount for this specific
      // cabin category + sail date (already scope-correct on the backend).
      const serverAmount = props.dealStatus?.hot_deal_details?.price_drop_amount;
      if (typeof serverAmount === 'number' && serverAmount > 0) {
        return Math.round(serverAmount);
      }
      // #60 cleared this price-drop baseline. The raw history max (chart
      // Highest) is the leak that still printed "Save up to $2,779".
      if (props.dealStatus?.hot_deal_type === 'price_drop') return 0;

      if (!props.currentPrice) return 0;

      // Fallback: same-cabin + same-sail-date scoped peak from price history.
      // NEVER use a global max(price) — that mixes cabin types and sail dates.
      const history = Array.isArray(props.priceHistory) ? props.priceHistory : [];
      const targetDate = toDateOnly(props.sailDate);
      const sameSailDate = !targetDate
        ? history
        : history.filter((p) => toDateOnly(p.sail_date) === targetDate);
      if (!sameSailDate.length) return 0;

      // Identify the cabin type of the current (min-price) observation at the
      // latest timestamp so the peak is scoped to the same cabin.
      let maxTs = -Infinity;
      for (const p of sameSailDate) {
        const ts = new Date(p.timestamp).getTime();
        if (Number.isFinite(ts) && ts > maxTs) maxTs = ts;
      }
      const atLatest = sameSailDate.filter(
        (p) => new Date(p.timestamp).getTime() === maxTs
      );
      let cabinType = null;
      let minPrice = Infinity;
      for (const p of atLatest) {
        const price = Number(p.price);
        if (!Number.isNaN(price) && price < minPrice) {
          minPrice = price;
          cabinType = p.cabin_type || 'Unknown';
        }
      }
      if (!cabinType) return 0;

      const sameCabin = sameSailDate.filter(
        (p) => (p.cabin_type || 'Unknown') === cabinType
      );
      const prices = sameCabin
        .map((p) => Number(p.price))
        .filter((n) => !Number.isNaN(n));
      if (!prices.length) return 0;

      const peak = Math.max(...prices);
      if (!peak || peak <= props.currentPrice) return 0;
      // A swapped/mislabeled collection day must not become "Save up to"
      // after the server has already omitted that baseline, or when this
      // sailing is not a price-drop deal and the chart is the only source.
      if (!peakIsCoherent(sameSailDate, cabinType, peak)) return 0;
      return Math.round(peak - props.currentPrice);
    });
    
    const bestBookingTime = computed(() => bookingTimeCopy(props.observedSeries));
    
    return {
      formatPriceAmount,
      pricePerNight,
      savings,
      bestBookingTime
    };
  }
};
</script>

<style scoped>
.pricing-enhancements {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.price-per-night,
.savings-calculator,
.booking-insight {
  background-color: var(--cs-d-surface-alt);
  border-radius: 8px;
  padding: 16px;
}

.price-per-night h4,
.savings-calculator h4,
.booking-insight h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  color: var(--cs-d-text);
}

.price-display {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.price-value {
  font-size: 28px;
  font-weight: bold;
  color: var(--cs-d-link);
}

.price-label {
  font-size: 14px;
  color: var(--cs-d-muted);
}

.savings-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.savings-amount {
  font-size: 20px;
  font-weight: bold;
  color: var(--cs-price);
}

.savings-detail {
  font-size: 12px;
  color: var(--cs-d-muted);
}

.savings-explainer {
  font-size: 12px;
  color: var(--cs-d-muted);
  font-style: italic;
  line-height: 1.5;
}

.insight-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.insight-content i {
  color: #fbbc05;
  font-size: 20px;
  margin-top: 2px;
}

.insight-content p {
  margin: 0;
  color: var(--cs-d-muted);
  line-height: 1.5;
}

@media (max-width: 768px) {
  .pricing-enhancements {
    gap: 16px;
  }
}
</style>

